import { formatMoney, formatDate } from '../../lib/utils'
import { mesAno } from '../../lib/ajustesImportacao'
import { ehParcelado, ESCOLHA_PADRAO, motivoSemTrocar, type EscolhaPar } from '../../lib/paresImportacao'
import { EscolhaEscopo } from './ImportarEscopo'
import type { AcaoPar, EscopoExclusao, ItemPreVisualizacaoCsv } from '../../types'

const parcelaTxt = (n?: number | null, t?: number | null) => (n != null && t != null ? `Parcela ${n}/${t}` : 'À vista')

function Lado({ titulo, descricao, data, valor, parcela, fatura, aviso }: {
  titulo: string; descricao: string; data: string; valor: number; parcela: string; fatura?: string; aviso?: string
}) {
  return (
    <div className="rounded-lg border border-line p-2 text-12">
      <p className="text-2xs uppercase text-muted">{titulo}</p>
      <p className="font-medium text-white">{descricao}</p>
      <p className="text-muted">{formatDate(data)} · {parcela}{fatura ? ` · fatura ${fatura}` : ''}</p>
      <p className="font-semibold text-white">{formatMoney(valor)}</p>
      {aviso && <p className="text-2xs text-warning">{aviso}</p>}
    </div>
  )
}

/** Um par: linha do arquivo x lançamento já existente na fatura, com a decisão (manter / trocar / são diferentes). */
function Par({ item, escolha, onEscolha, destinoPago }: {
  item: ItemPreVisualizacaoCsv
  escolha: EscolhaPar
  onEscolha: (e: EscolhaPar) => void
  destinoPago: boolean
}) {
  const c = item.candidatoPar!
  const semTrocar = motivoSemTrocar(item, destinoPago)
  const parcelado = ehParcelado(c.parcelaNumero, c.escopos)
  const nome = `par-${item.indice}`
  const opcao = (acao: AcaoPar, rotulo: string, motivoOff?: string | null) => (
    <label className={`flex items-start gap-2 text-13 ${motivoOff ? 'text-muted' : 'text-white'}`}>
      <input type="radio" className="mt-1" name={nome} checked={escolha.acao === acao} disabled={!!motivoOff} onChange={() => onEscolha({ ...escolha, acao })} />
      <span>{rotulo}{acao === 'manter' ? ' (padrão)' : ''}{motivoOff && <span className="block text-2xs text-warning">{motivoOff}</span>}</span>
    </label>
  )
  return (
    <div className="space-y-2 rounded-lg border border-line p-3">
      <div className="grid gap-2 sm:grid-cols-2">
        <Lado titulo="No arquivo" descricao={item.descricao} data={item.data} valor={item.valor} parcela={parcelaTxt(item.parcelaNumero, item.parcelaTotalNumero)} />
        <Lado
          titulo="Já lançado no app" descricao={c.descricao} data={c.data} valor={c.valor} parcela={parcelaTxt(c.parcelaNumero, c.parcelaTotalNumero)}
          fatura={mesAno(c.faturaMes, c.faturaAno)} aviso={c.vindoDaPluggy ? 'Veio do banco (Pluggy)' : undefined}
        />
      </div>
      {opcao('manter', 'Manter o existente e não importar a do arquivo')}
      {opcao('trocar', 'Trocar: importar a do arquivo e excluir o existente', semTrocar)}
      {opcao('diferentes', 'São diferentes: importar e manter os dois')}
      {escolha.acao === 'trocar' && parcelado && (
        <div className="pl-6">
          <EscolhaEscopo nome={`${nome}-escopo`} escopos={c.escopos} valor={escolha.escopo} onChange={(escopo: EscopoExclusao) => onEscolha({ ...escolha, escopo })} />
        </div>
      )}
    </div>
  )
}

/** Possíveis duplicadas: itens "novos" do arquivo que têm um lançamento de mesmo valor na fatura. */
export function ParesDuplicadas({ itens, escolhas, onEscolha, destinoPago }: {
  itens: ItemPreVisualizacaoCsv[]
  escolhas: Record<number, EscolhaPar>
  onEscolha: (indice: number, e: EscolhaPar) => void
  destinoPago: boolean
}) {
  if (itens.length === 0) return null
  return (
    <div className="space-y-2 rounded-lg border border-warning/50 p-3">
      <p className="text-13 font-medium text-white">Possíveis duplicadas ({itens.length})</p>
      <p className="text-12 text-muted">
        Estas linhas do arquivo não casaram pelo nome ou pela data, mas já existe um lançamento do mesmo valor na fatura. Escolha o que fazer com cada par.
      </p>
      {itens.map((item) => (
        <Par key={item.indice} item={item} escolha={escolhas[item.indice] ?? ESCOLHA_PADRAO} onEscolha={(e) => onEscolha(item.indice, e)} destinoPago={destinoPago} />
      ))}
    </div>
  )
}
